import {defineStore} from 'pinia'
import api from '@/services/api'
import {useComponentsStore} from '@/stores/components'
import {useDisplayOrdersStore} from '@/stores/displayOrders'
import {useItemIdsStore} from '@/stores/itemIds'
import {useAppStore} from '@/stores/appStore'

export const useItemDataStore = defineStore('itemData', {
    state: () => ({
        promises: {},
        pendingPreloads: {} // e.g. { "odp_courses:[1,2,3]": Promise }
    }),
    
    actions: {
        remove({tableName, id}) {
            if (
                this.promises[tableName] &&
                this.promises[tableName][id]
            ) {
                delete this.promises[tableName][id]
            }
        },
        
        isset({tableName, id}) {
            return this.promises[tableName]?.[id]
        },
        
        async get({tableName, id, refresh}) {
            // If the id is a number ensure it's an int. We need to check if it's a number because some components
            // allow strings to be provided as IDs, such as modulecomponents and sites.
            id = parseInt(id)
            
            if (!this.promises[tableName]) {
                this.promises[tableName] = {}
            }
            
            if (refresh && this.promises[tableName][id]) {
                delete this.promises[tableName][id]
            }
            
            if (!this.promises[tableName][id]) {
                this.promises[tableName][id] = (async () => {
                    if (
                        id === 0 ||
                        isNaN(id) // This prevents request 404 errors when no ID is provided.
                    ) {
                        // Providing a blank object when id===0 means that the object can be preset
                        // with default values e.g. content's pageId and page's navigation groups
                        return {}
                    } else {
                        try {
                            const obj = await api.get('api/component/' + tableName + '/' + id)
                            
                            if (obj.data) {
                                this.setDisplayOrder({
                                    tableName: tableName,
                                    obj: obj.data
                                })
                            }
                            
                            return obj.data
                        } catch (e) {
                            // We must resolve/return so that the calling script can continue.
                            return undefined
                        }
                    }
                })()
            }
            
            return this.promises[tableName][id]
        },
        
        async getAll({tableName}) {
            const itemIdsStore = useItemIdsStore()
            const ids = await itemIdsStore.get({tableName})
            return await this.preload({tableName, ids})
        },
        
        // Updates a promise's object resulting updates to all directives which reference the object.
        set({tableName, id, property, value}) {
            if (
                this.promises[tableName] &&
                this.promises[tableName][id]
            ) {
                this.promises[tableName][id]
                    .then((obj) => {
                        if (typeof property === 'object') {
                            Object.assign(obj, property)
                        } else {
                            obj[property] = value
                        }
                        
                        this.setDisplayOrder({
                            tableName: tableName,
                            obj: obj
                        })
                    })
            }
        },
        
        async insert({tableName, data}) {
            const response = await api.post(`api/component/${tableName}`, data)
            let item = response.data
            this.set({
                tableName: tableName,
                id: item.id,
                property: item,
            })
            return response
        },
        
        async update({tableName, id, data}) {
            const response = await api.patch(`api/component/${tableName}/${id}`, data)
            const property = response.data
            this.set({tableName, id, property})
            return response
        },
        
        setItems({tableName, objs}) {
            objs.forEach((obj) => {
                this.setItem({
                    tableName: tableName,
                    obj: obj
                })
            })
        },
        
        setItem({tableName, obj}) {
            if (!this.promises[tableName]) {
                this.promises[tableName] = {}
            }
            
            this.setDisplayOrder({
                tableName: tableName,
                obj: obj
            })
            
            let id = obj.id
            
            this.promises[tableName][id] = new Promise((resolve) => {
                resolve(obj)
            })
        },
        
        // Utility for pre-caching a collection of items to avoid the UI generating lots of individual requests when
        // ItemData.get is used for individual items. For example, the view factory uses it to preload all items to be
        // presented in the listing.
        async preload({tableName, ids}) {
            if (!ids) {
                return
            }
            
            // ids may contain some ids which have already been set. To reduce the request size we only want unset IDs.
            // So create a new array only containing the unset IDs.
            let unsetIds = []
            ids.forEach((id) => {
                id = parseInt(id)
                if (
                    this.promises[tableName] &&
                    this.promises[tableName][id]
                ) {
                    return
                }
                unsetIds.push(id)
            })
            
            if (unsetIds.length) {
                const obj = await api.post(`api/component/${tableName}`, {
                    id: unsetIds,
                }, {
                    headers: {'X-Http-Method-Override': 'GET'},
                })
                
                let objs = obj.data.items
                objs.forEach((obj) => {
                    // If a request is sent for an item which does not exist then the
                    // response's obj.data will be set to ""
                    if (obj !== "") {
                        this.setItem({
                            tableName: tableName,
                            obj: obj,
                        })
                    }
                })
            }
            
            const promiseArray = ids.map((id) =>
                this.get({
                    tableName: tableName,
                    id: id,
                })
            )
            
            return Promise.all(promiseArray)
        },
        
        async archive({tableName, id, doArchive}) {
            const appStore = useAppStore()
            
            const response = await api.post('api/ListingController/archive', {
                tableName: tableName,
                ids: [id], // ListingController/archive expects an array
                archive: doArchive,
            })
            
            this.set({
                tableName: tableName,
                id: id,
                property: 'isArchived',
                value: doArchive,
            })
            
            appStore.cacheNeedsClearing = true
            
            return response
        },
        
        async delete({tableName, id}) {
            const appStore = useAppStore()
            
            await api.delete(`api/component/${tableName}/${id}`)
            
            appStore.cacheNeedsClearing = true
            
            this.remove({tableName, id})
            
            if (tableName === 'modulecomponents') {
                const componentsStore = useComponentsStore()
                await componentsStore.init(this)
            }
        },
        
        setDisplayOrder({tableName, obj}) {
            if (obj.displayOrder !== undefined) {
                const displayOrdersStore = useDisplayOrdersStore()
                displayOrdersStore.set({
                    tableName: tableName,
                    id: obj.id,
                    displayOrder: obj.displayOrder
                })
            }
        },
        
        async updateDisplayOrders({componentId}) {
            const componentsStore = useComponentsStore()
            let component = componentsStore.items.find(o => o.id === componentId)
            
            if (component?.showDisplayOrder) {
                let tableName = component.tableName
                
                const res = await api.get('api/component/' + tableName, {
                    params: {
                        fields: ['id', 'displayOrder'],
                    }
                })
                
                res.data.items.forEach((obj) => {
                    this.set({
                        tableName: tableName,
                        id: obj.id,
                        property: 'displayOrder',
                        value: obj.displayOrder,
                    })
                })
            }
        }
    }
})